import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { appleSignIn } from '../../../api/auth/appleSignIn';
import { googleSignIn } from '../../../api/auth/googleSignIn';
import { signInWithEmail } from '../../../api/auth/signInWithEmail';
import { View } from '../../../common/constants/ViewEnum';
import { useAuthContext } from '../../../common/context/AuthContext';
import { trackEvent } from '../../../common/utils/analytics';
import SecondaryButton from '../../buttons/SecondaryButton';
import PrimaryInput from '../../inputs/PrimaryInput';
import PopsizeSpinningLoaderLarge from '../../loader/PopsizeSpinningLoaderLarge';
import PrimaryParagraph from '../../texts/PrimaryParagraph';
import PrimaryTitle from '../../texts/PrimaryTitle';
import { PopsizeViewProps } from '../types';

const LoginView: FC<PopsizeViewProps> = (props) => {
	const { persistAuth } = useAuthContext();
	const { t } = useTranslation();

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);

	const [loginLoading, setLoginLoading] = useState(false);
	const [loginError, setLoginError] = useState('');

	const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);


	const handleLogin = async () => {
		setLoginLoading(true);
		setLoginError('');
		try {
			const loginResponse = await signInWithEmail(email, password);
			persistAuth(loginResponse.token);

			// Show full-screen loader after success
		setShowFullScreenLoader(true);
		} catch (error: any) {
			console.error('Error during login:', error);
			setLoginError(t('user_login_email_password_error') || 'An error occurred during login.');
		} finally {
			setLoginLoading(false);
		}
	};

	const handleGoogleSignIn = async () => {
		trackEvent('google_signup_clicked');
		setLoginLoading(true);
		setLoginError('');
		try {
			const googleSignInResponse = await googleSignIn();
			persistAuth(googleSignInResponse.token);
			setShowFullScreenLoader(true);
		} catch (error: any) {
			console.error('Error during login:', error);
			setLoginError(t('sign_in_with_google_error') || 'An error occurred while signing in with Google.');
		} finally {
			setLoginLoading(false);
		}
	};

	const handleAppleSignIn = async () => {
		setLoginLoading(true);
		setLoginError('');
		try {
			const appleSignInResponse = await appleSignIn();
			persistAuth(appleSignInResponse.token);
			setShowFullScreenLoader(true);
		} catch (error: any) {
			console.error('Error during login:', error);
			setLoginError(t('sign_in_with_apple_error') || 'An error occurred while signing in with Apple.');
		} finally {
			setLoginLoading(false);
		}
	};

	if (showFullScreenLoader) {
		return (
			<div className="pop-h-full pop-w-screen pop-flex pop-items-center pop-justify-center pop-animate-fadeIn">
				<PopsizeSpinningLoaderLarge username={''} />
			</div>
		);
	}

	return (
	<div className="pop-fadeIn pop-flex pop-flex-col pop-justify-between pop-h-full pop-p-4 pop-gap-4">
		{/* 🔹 1. Title */}
		<PrimaryTitle className="pop-text-center">{t('connect_account')}</PrimaryTitle>

		{/* 🔹 2. Email + Password + Forgot Password */}
		<div className="pop-flex pop-flex-col pop-gap-2">
			<PrimaryInput
				type="text"
				placeholder="Email or username"
				value={email}
				onChange={(e) => setEmail(e.target.value)}
				className="pop-w-full"
			/>
			<PrimaryInput
				type={showPassword ? 'text' : 'password'}
				placeholder="Password"
				value={password}
				onChange={(e) => setPassword(e.target.value)}
				className="pop-w-full"
				icon={
					<button
						type="button"
						className="pop-absolute pop-right-5 pop-top-6 pop-text-sm"
						onClick={() => setShowPassword(!showPassword)}
					>
						{showPassword ? (
							<FaEyeSlash className="pop-text-popsizeSecondary90" size={16} />
						) : (
							<FaEye className="pop-text-popsizeSecondary90" size={16} />
						)}
					</button>
				}
			/>

			{/* Forgot password link */}
			<div className="pop-flex pop-justify-end pop-my-2">
				<button
					className="pop-text-sm pop-text-popsizePrimary90 hover:pop-underline"
					onClick={() => props.toggleView(View.RestPassword)}
				>
					{t('forgot_password')}
				</button>
			</div>

			{/* Login error */}
			{loginError && (
				<PrimaryParagraph className="pop-text-red-500 pop-text-center pop-text-sm">
					{loginError}
				</PrimaryParagraph>
			)}

			{/* Login button */}
			<SecondaryButton
				className="pop-bg-popsizeGradientSecondary9050 pop-text-white pop-border-none pop-font-bold pop-text-base"
				onClick={handleLogin}
				isLoading={loginLoading}
				isDisabled={loginLoading || !email || !password}
				fullButtonSecondary={true}
			>
				{t('log_in')}
			</SecondaryButton>
			<div className="pop-mt-4">
			<SecondaryButton
				className="pop-bg-white pop-text-black pop-border-gray-300 pop-flex pop-items-center pop-justify-center pop-gap-2"
				onClick={handleGoogleSignIn}
				isDisabled={loginLoading}
				fullButtonSecondary={true}
			>
				<FcGoogle size={20} />
				<span>{t('sign_in_with_google')}</span>
			</SecondaryButton>
			</div>
		</div>

		{/* 🔹 3. Social sign-in buttons */}
		{/*<div className="pop-flex pop-flex-col pop-gap-2">
			<SecondaryButton
				className="pop-bg-white pop-text-black pop-border-gray-300 pop-flex pop-items-center pop-justify-center pop-gap-2"
				onClick={handleGoogleSignIn}
				isDisabled={loginLoading}
				fullButtonSecondary={true}
			>
				<FcGoogle size={20} />
				<span>{t('sign_in_with_google')}</span>
			</SecondaryButton>
			<SecondaryButton
				className="pop-bg-black pop-text-white pop-flex pop-items-center pop-justify-center pop-gap-2 hover:pop-text-gray-950"
				onClick={handleAppleSignIn}
				isDisabled={loginLoading}
				fullButtonSecondary={true}
			>
				<FaApple size={20} />
				<span>{t('sign_in_with_apple')}</span>
			</SecondaryButton>
		</div>*/}

		{/* 🔹 4. No account yet */}
		<PrimaryParagraph className="pop-text-center">
			{t('no_account_yet')}{' '}
			<span
				className="pop-text-popsizeSecondary90 pop-cursor-pointer hover:pop-underline"
				onClick={() => props.toggleView(View.Register)}
			>
				{t('create_account')}
			</span>
		</PrimaryParagraph>
	</div>
);
};

export default LoginView;
